import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import type {
    CreateAttributeDefinitionMutation,
    CreateAttributeDefinitionMutationVariables,
    DeleteAttributeAssetMutation,
    DeleteAttributeAssetMutationVariables,
    ReorderAttributeImagesMutation,
    ReorderAttributeImagesMutationVariables,
    SetAttributeAssetCaptionMutation,
    SetAttributeAssetCaptionMutationVariables,
    SetAttributeTextMutation,
    SetAttributeTextMutationVariables,
} from '@/graphql/types';
import {
    CreateAttributeDefinitionDocument,
    DeleteAttributeAssetDocument,
    ReorderAttributeImagesDocument,
    SetAttributeAssetCaptionDocument,
    SetAttributeTextDocument,
} from '@/graphql/types';
import type { AttributeEntityType, AttributeEntry, AttributeValueGraphQLValue } from '../contracts';
import { toAttributesEntityGraphQLValue } from '../contracts';
import { adaptAttributeEntry } from './useAttributesData';

interface AttributeMutationTarget {
    entityType: AttributeEntityType;
    entityId: string;
    key: string;
}

type AttributeEntryMutationPayload = {
    attributeEntry?: unknown;
};

function requireUpdatedEntry(
    payload: AttributeEntryMutationPayload | null | undefined
): AttributeEntry {
    if (!payload?.attributeEntry) {
        throw new Error('Attributes mutation did not return an updated AttributeEntry.');
    }

    return adaptAttributeEntry(payload.attributeEntry as never);
}

function entityTypeVariable<TVariables extends { entityType: unknown }>(
    entityType: AttributeEntityType
): TVariables['entityType'] {
    return toAttributesEntityGraphQLValue(entityType) as TVariables['entityType'];
}

export function useAttributeMutations() {
    const [setTextMutation, setTextState] = useMutation<
        SetAttributeTextMutation,
        SetAttributeTextMutationVariables
    >(SetAttributeTextDocument);
    const [setCaptionMutation, setCaptionState] = useMutation<
        SetAttributeAssetCaptionMutation,
        SetAttributeAssetCaptionMutationVariables
    >(SetAttributeAssetCaptionDocument);
    const [reorderMutation, reorderState] = useMutation<
        ReorderAttributeImagesMutation,
        ReorderAttributeImagesMutationVariables
    >(ReorderAttributeImagesDocument);
    const [deleteAssetMutation, deleteAssetState] = useMutation<
        DeleteAttributeAssetMutation,
        DeleteAttributeAssetMutationVariables
    >(DeleteAttributeAssetDocument);
    const [createDefinitionMutation, createDefinitionState] = useMutation<
        CreateAttributeDefinitionMutation,
        CreateAttributeDefinitionMutationVariables
    >(CreateAttributeDefinitionDocument);

    const createDefinition = useCallback(
        async (
            target: Omit<AttributeMutationTarget, 'key'>,
            input: {
                label: string;
                valueType: AttributeValueGraphQLValue;
            }
        ) => {
            const result = await createDefinitionMutation({
                variables: {
                    entityType: entityTypeVariable<CreateAttributeDefinitionMutationVariables>(
                        target.entityType
                    ),
                    entityId: target.entityId,
                    label: input.label,
                    valueType:
                        input.valueType as CreateAttributeDefinitionMutationVariables['valueType'],
                },
            });

            return requireUpdatedEntry(result.data?.createAttributeDefinition);
        },
        [createDefinitionMutation]
    );

    const setText = useCallback(
        async (target: AttributeMutationTarget, textValue: string) => {
            const result = await setTextMutation({
                variables: {
                    entityType: entityTypeVariable<SetAttributeTextMutationVariables>(
                        target.entityType
                    ),
                    entityId: target.entityId,
                    key: target.key,
                    textValue,
                },
            });

            return requireUpdatedEntry(result.data?.setAttributeText);
        },
        [setTextMutation]
    );

    const setAssetCaption = useCallback(
        async (target: AttributeMutationTarget, assetId: string, caption: string) => {
            const result = await setCaptionMutation({
                variables: {
                    entityType: entityTypeVariable<SetAttributeAssetCaptionMutationVariables>(
                        target.entityType
                    ),
                    entityId: target.entityId,
                    key: target.key,
                    assetId,
                    caption,
                },
            });

            return requireUpdatedEntry(result.data?.setAttributeAssetCaption);
        },
        [setCaptionMutation]
    );

    const reorderImages = useCallback(
        async (target: AttributeMutationTarget, assetIds: string[]) => {
            const result = await reorderMutation({
                variables: {
                    entityType: entityTypeVariable<ReorderAttributeImagesMutationVariables>(
                        target.entityType
                    ),
                    entityId: target.entityId,
                    key: target.key,
                    assetIds,
                },
            });

            return requireUpdatedEntry(result.data?.reorderAttributeImages);
        },
        [reorderMutation]
    );

    const deleteAsset = useCallback(
        async (target: AttributeMutationTarget, assetId: string) => {
            const result = await deleteAssetMutation({
                variables: {
                    entityType: entityTypeVariable<DeleteAttributeAssetMutationVariables>(
                        target.entityType
                    ),
                    entityId: target.entityId,
                    key: target.key,
                    assetId,
                },
            });

            return requireUpdatedEntry(result.data?.deleteAttributeAsset);
        },
        [deleteAssetMutation]
    );

    return {
        createDefinition,
        setText,
        setAssetCaption,
        reorderImages,
        deleteAsset,
        isMutating:
            setTextState.loading ||
            createDefinitionState.loading ||
            setCaptionState.loading ||
            reorderState.loading ||
            deleteAssetState.loading,
    };
}
